// frontend/src/components/common/ImageUpload/ImageUploadComponent.tsx
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { 
  Upload, 
  X, 
  Image as ImageIcon, 
  Star, 
  Edit3, 
  Trash2, 
  Plus,
  AlertCircle,
  CheckCircle,
  Loader2
} from 'lucide-react';
import { httpClient } from '@/lib/httpClient';
import type { ImageFile, ImageUploadProps, ApiResponse } from '@/types/imageUpload';

const ImageUploadComponent: React.FC<ImageUploadProps> = ({
  multiple = false,
  maxFiles = 5,
  maxFileSize = 5 * 1024 * 1024, // 5MB
  acceptedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  entityType,
  entityId,
  allowPrimary = false,
  allowCaptions = false,
  allowCrop = false,
  compressionQuality = 0.8,
  requiredDimensions,
  initialImages = [],
  className = '',
  dropzoneClassName = '',
  previewClassName = '',
  onUpload,
  onDelete,
  onUpdateCaption,
  onSetPrimary,
  onChange,
  showProgress = true,
  showPreview = true,
  dragDropText = 'Drag & drop images here',
  browseText = 'Browse files',
  compact = false
}) => {
  const [images, setImages] = useState<ImageFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [editingCaption, setEditingCaption] = useState<string | null>(null);
  const [captionValue, setCaptionValue] = useState('');
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropzoneRef = useRef<HTMLDivElement>(null);

  // FIXED: Update images when initialImages changes (with proper comparison)
  useEffect(() => {
    // Only update if initialImages actually changed (deep comparison by checking IDs)
    const currentIds = images.map(img => img.id).sort().join(',');
    const newIds = initialImages.map(img => img.id).sort().join(',');
    
    if (currentIds !== newIds) {
      console.log('🔄 ImageUpload: initialImages changed', initialImages);
      setImages(initialImages);
    }
  }, [initialImages]); // Remove 'images' from dependency

  // FIXED: Notify parent of changes only when needed (prevent infinite loops)
  useEffect(() => {
    // Only notify if there's actually a change and it's not from initialImages
    if (onChange && images.length > 0) {
      const timeoutId = setTimeout(() => {
        onChange(images);
      }, 100); // Debounce to prevent rapid firing
      
      return () => clearTimeout(timeoutId);
    }
  }, [images]); // Remove onChange from dependency

  // File validation
  const validateFile = useCallback((file: File): string | null => {
    if (!acceptedTypes.includes(file.type)) {
      return `File type ${file.type} is not supported. Allowed types: ${acceptedTypes.join(', ')}`;
    }
    
    if (file.size > maxFileSize) {
      return `File size ${(file.size / 1024 / 1024).toFixed(2)}MB exceeds maximum ${(maxFileSize / 1024 / 1024).toFixed(2)}MB`;
    }
    
    return null;
  }, [acceptedTypes, maxFileSize]);

  // Image dimension validation
  const validateImageDimensions = useCallback((file: File): Promise<string | null> => {
    return new Promise((resolve) => {
      if (!requiredDimensions) {
        resolve(null);
        return;
      }

      const img = new Image();
      img.onload = () => {
        const { width, height } = img;
        
        if (requiredDimensions.min) {
          if (width < requiredDimensions.min.width || height < requiredDimensions.min.height) {
            resolve(`Image dimensions ${width}x${height} are below minimum ${requiredDimensions.min.width}x${requiredDimensions.min.height}`);
            return;
          }
        }
        
        if (requiredDimensions.max) {
          if (width > requiredDimensions.max.width || height > requiredDimensions.max.height) {
            resolve(`Image dimensions ${width}x${height} exceed maximum ${requiredDimensions.max.width}x${requiredDimensions.max.height}`);
            return;
          }
        }
        
        resolve(null);
      };
      img.src = URL.createObjectURL(file);
    });
  }, [requiredDimensions]);

  // Process files
  const processFiles = useCallback(async (fileList: FileList) => {
    const files = Array.from(fileList);
    const newImages: ImageFile[] = [];
    
    // Check total file count limit
    if (!multiple && files.length > 1) {
      alert('Only one file is allowed');
      return;
    }
    
    if (multiple && images.length + files.length > maxFiles) {
      alert(`Maximum ${maxFiles} files allowed`);
      return;
    }

    for (const file of files) {
      // Validate file
      const fileError = validateFile(file);
      if (fileError) {
        alert(fileError);
        continue;
      }

      // Validate dimensions
      const dimensionError = await validateImageDimensions(file);
      if (dimensionError) {
        alert(dimensionError);
        continue;
      }

      // Create image object
      const imageFile: ImageFile = {
        id: `temp_${Date.now()}_${Math.random()}`,
        file: file,
        url: URL.createObjectURL(file),
        isUploading: false,
        isUploaded: false,
        progress: 0
      };

      newImages.push(imageFile);
    }

    if (multiple) {
      setImages(prev => [...prev, ...newImages]);
    } else {
      setImages(newImages);
    }
  }, [images.length, maxFiles, multiple, validateFile, validateImageDimensions]);

  // Handle file upload to server
  const handleUpload = useCallback(async () => {
    const filesToUpload = images.filter(img => img.file && !img.isUploaded);
    
    if (filesToUpload.length === 0) return;
    
    setIsUploading(true);

    try {
      // Update uploading status
      setImages(prev => prev.map(img => 
        filesToUpload.find(f => f.id === img.id) 
          ? { ...img, isUploading: true, progress: 0 }
          : img
      ));

      // Call parent upload handler if provided
      if (onUpload) {
        await onUpload(filesToUpload);
      } else {
        // Default upload logic
        for (const imageFile of filesToUpload) {
          await uploadSingleFile(imageFile);
        }
      }

      // Mark as uploaded
      setImages(prev => prev.map(img => 
        filesToUpload.find(f => f.id === img.id)
          ? { ...img, isUploading: false, isUploaded: true, progress: 100 }
          : img
      ));

    } catch (error) {
      console.error('Upload error:', error);
      
      setImages(prev => prev.map(img => 
        filesToUpload.find(f => f.id === img.id)
          ? { ...img, isUploading: false, error: 'Upload failed' }
          : img
      ));
    } finally {
      setIsUploading(false);
    }
  }, [images, onUpload]);

  // Upload single file using your API
  const uploadSingleFile = useCallback(async (imageFile: ImageFile) => {
    if (!imageFile.file) return;

    const formData = new FormData();
    formData.append('image', imageFile.file);
    
    if (entityType) formData.append('entity_type', entityType);
    if (entityId) formData.append('entity_id', String(entityId));

    // FIXED: Use correct endpoint
    const response = await httpClient.post('/media/upload', formData) as any;

    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.message || 'Upload failed');
    }
  }, [entityType, entityId]);

  // Remove image
  const removeImage = useCallback(async (imageId: string) => {
    try {
      if (onDelete) {
        await onDelete(imageId);
      } else {
        // If it's a temporary image (not uploaded yet), just remove from state
        const image = images.find(img => img.id === imageId);
        if (!image?.isUploaded && !image?.media_id) {
          setImages(prev => prev.filter(img => img.id !== imageId));
          return;
        }
        
        // For uploaded images, call API
        if (image?.media_id) {
          const response = await httpClient.delete(`/media/${image.media_id}`) as any;
          
          if (!response.success) {
            throw new Error(response.message || 'Delete failed');
          }
        }
      }
      
      setImages(prev => prev.filter(img => img.id !== imageId));
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete image');
    }
  }, [images, onDelete]);

  // Set primary image
  const setPrimaryImage = useCallback(async (imageId: string) => {
    try {
      const image = images.find(img => img.id === imageId);
      
      if (onSetPrimary) {
        await onSetPrimary(imageId);
      } else if (image?.media_id) {
        const response = await httpClient.post(`/media/${image.media_id}/set-primary`) as any;
        
        if (!response.success) {
          throw new Error(response.message || 'Set primary failed');
        }
      }
      
      setImages(prev => prev.map(img => ({
        ...img,
        isPrimary: img.id === imageId
      })));
    } catch (error) {
      console.error('Set primary error:', error);
      alert('Failed to set primary image');
    }
  }, [images, onSetPrimary]);

  // Update caption
  const updateCaption = useCallback(async (imageId: string, caption: string) => {
    try {
      const image = images.find(img => img.id === imageId);
      
      if (onUpdateCaption) {
        await onUpdateCaption(imageId, caption);
      } else if (image?.media_id) {
        const response = await httpClient.put(`/media/${image.media_id}`, { caption }) as any;
        
        if (!response.success) {
          throw new Error(response.message || 'Caption update failed');
        }
      }
      
      setImages(prev => prev.map(img => 
        img.id === imageId ? { ...img, caption } : img
      ));
      
      setEditingCaption(null);
      setCaptionValue('');
    } catch (error) {
      console.error('Caption update error:', error);
      alert('Failed to update caption');
    }
  }, [images, onUpdateCaption]);

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (dropzoneRef.current && !dropzoneRef.current.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      processFiles(files);
    }
  }, [processFiles]);

  // File input handler
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFiles(files);
    }
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [processFiles]);

  // FIXED: Debug logging (throttled to prevent spam)
  const debugLogRef = useRef<number>(0);
  useEffect(() => {
    debugLogRef.current++;
    const logId = debugLogRef.current;
    
    setTimeout(() => {
      if (debugLogRef.current === logId) { // Only log if it's the latest
        console.log('🖼️ ImageUpload Render:', {
          imagesCount: images.length,
          showPreview,
          hasInitialImages: initialImages.length,
          renderCount: logId
        });
      }
    }, 100);
  }, [images.length, showPreview, initialImages.length]);

  return (
    <div className={`image-upload-container ${className}`}>
      {/* Upload Zone */}
      <div
        ref={dropzoneRef}
        className={`
          relative border-2 border-dashed rounded-lg text-center transition-colors cursor-pointer
          ${compact ? 'p-4' : 'p-8'}
          ${isDragOver 
            ? 'border-blue-500 bg-blue-50' 
            : 'border-gray-300 hover:border-gray-400'
          }
          ${dropzoneClassName}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedTypes.join(',')}
          multiple={multiple}
          onChange={handleFileSelect}
          className="hidden"
        />
        
        {!compact && <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />}
        
        <div className={compact ? 'space-y-1' : 'space-y-2'}>
          {!compact && <p className="text-lg text-gray-600">{dragDropText}</p>}
          
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }}
            className={`inline-flex items-center border border-gray-300 rounded-md shadow-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
              compact ? 'px-3 py-1 text-xs' : 'px-4 py-2 text-sm'
            }`}
          >
            <Plus className={`mr-2 ${compact ? 'h-3 w-3' : 'h-4 w-4'}`} />
            {browseText}
          </button>
        </div>
        
        {!compact && (
          <div className="mt-4 text-xs text-gray-500">
            <p>Supported formats: {acceptedTypes.map(type => type.replace('image/', '')).join(', ')}</p>
            <p>Maximum file size: {(maxFileSize / 1024 / 1024).toFixed(1)}MB</p>
            {multiple && <p>Maximum files: {maxFiles}</p>}
          </div>
        )}
      </div>

      {/* Upload Button */}
      {images.some(img => img.file && !img.isUploaded) && (
        <div className={compact ? 'mt-2 text-center' : 'mt-4 text-center'}>
          <button
            onClick={handleUpload}
            disabled={isUploading}
            className={`inline-flex items-center border border-transparent font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed ${
              compact ? 'px-3 py-1 text-xs' : 'px-6 py-2 text-sm'
            }`}
          >
            {isUploading ? (
              <>
                <Loader2 className={`mr-2 animate-spin ${compact ? 'h-3 w-3' : 'h-4 w-4'}`} />
                Uploading...
              </>
            ) : (
              <>
                <Upload className={`mr-2 ${compact ? 'h-3 w-3' : 'h-4 w-4'}`} />
                Upload {images.filter(img => img.file && !img.isUploaded).length} file(s)
              </>
            )}
          </button>
        </div>
      )}

      {/* FIXED: Image Previews with better error handling */}
      {showPreview && images.length > 0 && (
        <div className={`${compact ? 'mt-3 grid grid-cols-3 gap-2' : 'mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4'} ${previewClassName}`}>
          {images.map((image) => (
            <div key={image.id} className="relative group">
              <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                <img
                  src={image.url}
                  alt={image.caption || 'Uploaded image'}
                  className="w-full h-full object-cover"
                  onLoad={() => console.log('✅ Image loaded:', image.url)}
                  onError={(e) => {
                    console.error('❌ Image failed to load:', image.url);
                    // Show broken image placeholder
                    e.currentTarget.style.display = 'none';
                  }}
                />
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-opacity">
                  <div className="absolute top-2 right-2 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {allowPrimary && (
                      <button
                        onClick={() => setPrimaryImage(image.id)}
                        className={`p-1 rounded transition-colors ${
                          image.isPrimary 
                            ? 'bg-yellow-500 text-white' 
                            : 'bg-white text-gray-600 hover:bg-yellow-500 hover:text-white'
                        }`}
                        title={image.isPrimary ? 'Primary image' : 'Set as primary'}
                      >
                        <Star className="h-4 w-4" />
                      </button>
                    )}
                    
                    {allowCaptions && (
                      <button
                        onClick={() => {
                          setEditingCaption(image.id);
                          setCaptionValue(image.caption || '');
                        }}
                        className="p-1 bg-white text-gray-600 rounded hover:bg-blue-500 hover:text-white transition-colors"
                        title="Edit caption"
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                    )}
                    
                    <button
                      onClick={() => removeImage(image.id)}
                      className="p-1 bg-white text-gray-600 rounded hover:bg-red-500 hover:text-white transition-colors"
                      title="Delete image"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Upload Progress */}
              {showProgress && image.isUploading && (
                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 rounded-b-lg">
                  <div 
                    className="h-2 bg-blue-500 transition-all duration-300 rounded-b-lg" 
                    style={{ width: `${image.progress || 0}%` }} 
                  />
                </div>
              )}

              {/* Status Icons */}
              <div className="absolute bottom-2 left-2">
                {image.isUploaded ? (
                  <CheckCircle className="h-5 w-5 text-green-500 bg-white rounded-full" />
                ) : image.error ? (
                  <AlertCircle className="h-5 w-5 text-red-500 bg-white rounded-full" />
                ) : image.isUploading ? (
                  <Loader2 className="h-5 w-5 text-blue-500 bg-white rounded-full animate-spin" />
                ) : null}
              </div>

              {/* Primary Badge */}
              {image.isPrimary && (
                <div className="absolute top-2 left-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-500 text-white">
                    <Star className="h-3 w-3 mr-1" />
                    Primary
                  </span>
                </div>
              )}

              {/* Caption */}
              {allowCaptions && (
                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 rounded-b-lg p-2">
                  {editingCaption === image.id ? (
                    <div className="flex space-x-1">
                      <input
                        type="text"
                        value={captionValue}
                        onChange={(e) => setCaptionValue(e.target.value)}
                        className="flex-1 text-xs px-2 py-1 rounded"
                        placeholder="Enter caption..."
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            updateCaption(image.id, captionValue);
                          }
                        }}
                        autoFocus
                      />
                      <button
                        onClick={() => updateCaption(image.id, captionValue)}
                        className="text-xs px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                      >
                        Save
                      </button>
                    </div>
                  ) : (
                    <p className="text-xs text-white truncate">
                      {image.caption || 'No caption'}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Debug Information (remove in production) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="mt-4 p-4 bg-gray-100 rounded-lg text-xs">
          <p><strong>Debug:</strong></p>
          <p>Images in state: {images.length}</p>
          <p>Show preview: {showPreview ? 'Yes' : 'No'}</p>
          <p>Initial images: {initialImages.length}</p>
        </div>
      )}
    </div>
  );
};

export default ImageUploadComponent;